import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type CustomerThemeMode = 'light' | 'dark' | 'system';

/**
 * D-130 rule 6 — the six customer-portal skins. Presentation only: a skin
 * changes design tokens and (in the four layout files) navigation placement,
 * and NOTHING else. It can never alter routes, permissions, location scope,
 * API calls or parameters, form fields, workflow states or available actions.
 * `customer-theme-invariance.test.tsx` proves that per render.
 */
export type CustomerSkin = 'forge' | 'aurora' | 'command' | 'pure' | 'horizon' | 'pulse';

export const CUSTOMER_SKINS: readonly CustomerSkin[] = [
  'forge',
  'aurora',
  'command',
  'pure',
  'horizon',
  'pulse',
] as const;

/** Forge is the default (D-130 rule 6). */
export const DEFAULT_CUSTOMER_SKIN: CustomerSkin = 'forge';

/** Product names and one-line descriptions. Never "Theme N" / "Concept N". */
export const CUSTOMER_SKIN_META: Record<CustomerSkin, { name: string; description: string }> = {
  forge: { name: 'Forge', description: 'Premium command experience for service teams' },
  aurora: { name: 'Aurora', description: 'Clean, polished and business-focused' },
  command: { name: 'Command', description: 'Built for operational control' },
  pure: { name: 'Pure', description: 'Minimal, calm and distraction-free' },
  horizon: { name: 'Horizon', description: 'Modern, visual and customer-first' },
  pulse: { name: 'Pulse', description: 'Optimized for fast mobile actions' },
};

interface CustomerThemeContextValue {
  mode: CustomerThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: CustomerThemeMode) => void;
  /** D-130 rule 6 — the active skin. Orthogonal to `mode`. */
  skin: CustomerSkin;
  setSkin: (skin: CustomerSkin) => void;
}

const CustomerThemeContext = createContext<CustomerThemeContextValue | null>(null);

const STORAGE_KEY = 'customer-portal:theme-mode';
/** D-130 rule 6 / readiness Q5 — per-device, alongside the mode key. No backend
 * column, no API: a skin is a device preference, not authorization data. */
const SKIN_STORAGE_KEY = 'customer-portal:skin';
const ROOT_CLASS = 'customer-portal-root';
const DARK_CLASS = 'customer-dark';
const SKIN_ATTRIBUTE = 'data-skin';

function getSystemMode(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveMode(mode: CustomerThemeMode): 'light' | 'dark' {
  return mode === 'system' ? getSystemMode() : mode;
}

export function isCustomerSkin(value: unknown): value is CustomerSkin {
  return typeof value === 'string' && (CUSTOMER_SKINS as readonly string[]).includes(value);
}

function getRootEl(): HTMLElement | null {
  return document.getElementById('root');
}

/** Reads persisted state defensively: private mode, cleared storage and a
 * tampered value all fall back to the documented default rather than throwing. */
function readStored<T>(key: string, isValid: (v: unknown) => v is T, fallback: T): T {
  try {
    const stored = window.localStorage.getItem(key);
    return isValid(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

function isMode(value: unknown): value is CustomerThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Customer Portal theme provider — UX Phase A (2026-07-10), extended with the
 * six-skin system in D-130 Phase 6B (2026-09-06).
 *
 * Deliberately isolated from the shared staff/Platform `ThemeProvider`
 * (src/providers/ThemeProvider.tsx): different storage keys, different DOM
 * target (#root, never <html>), different class name (`customer-dark`, never
 * `.dark`). This mirrors D-050's isolation discipline (separate auth store,
 * separate axios instance) applied to theme state, and is the fix for the
 * root cause of the Phase 7 "Pagination dark-theme leak" bug — that bug
 * existed precisely because Customer Portal components had no theme system
 * of their own and depended on the staff `.dark` class never being present.
 * Now it can be present (staff dark mode) with zero visual effect here,
 * because Customer Portal tokens never read `.dark` — only `.customer-dark`
 * on `#root`, which only this component ever sets.
 *
 * The skin is applied as `data-skin` on that SAME element, so the token
 * cascade is `#root.customer-portal-root[data-skin="…"]` and, for dark,
 * `#root.customer-portal-root[data-skin="…"].customer-dark`. Two independent
 * axes: six skins × three modes.
 */
export function CustomerThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<CustomerThemeMode>(() =>
    readStored(STORAGE_KEY, isMode, 'system'),
  );
  const [skin, setSkinState] = useState<CustomerSkin>(() =>
    readStored(SKIN_STORAGE_KEY, isCustomerSkin, DEFAULT_CUSTOMER_SKIN),
  );

  const resolvedMode = useMemo(() => resolveMode(mode), [mode]);

  useEffect(() => {
    const root = getRootEl();
    if (!root) return;
    root.classList.add(ROOT_CLASS);
    root.classList.toggle(DARK_CLASS, resolvedMode === 'dark');
  }, [resolvedMode]);

  useEffect(() => {
    const root = getRootEl();
    if (!root) return;
    root.classList.add(ROOT_CLASS);
    root.setAttribute(SKIN_ATTRIBUTE, skin);
  }, [skin]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const root = getRootEl();
      if (!root) return;
      root.classList.toggle(DARK_CLASS, getSystemMode() === 'dark');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const setMode = useCallback((next: CustomerThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode / blocked): the choice still applies
      // to this session; it simply does not survive a reload.
    }
  }, []);

  const setSkin = useCallback((next: CustomerSkin) => {
    setSkinState(next);
    try {
      window.localStorage.setItem(SKIN_STORAGE_KEY, next);
    } catch {
      // Same rationale as setMode.
    }
  }, []);

  const value = useMemo<CustomerThemeContextValue>(
    () => ({ mode, resolvedMode, setMode, skin, setSkin }),
    [mode, resolvedMode, setMode, skin, setSkin],
  );

  return <CustomerThemeContext.Provider value={value}>{children}</CustomerThemeContext.Provider>;
}

export function useCustomerTheme(): CustomerThemeContextValue {
  const ctx = useContext(CustomerThemeContext);
  if (ctx === null) {
    throw new Error('useCustomerTheme must be used within CustomerThemeProvider');
  }
  return ctx;
}
